import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  FileTooLargeError,
  friendlyLoadError,
  importFile,
  normalizeSourceFormat,
  ProImportLockedError,
  UnknownFormatError,
  type LoadedDocument,
} from './session';
import { track } from '../analytics/client';
import { bucketCount, bucketCueCount, type ImportErrorKind } from '../analytics/catalog';
import {
  autosaveSupported,
  clearAutosave,
  loadAutosave,
  saveAutosave,
  type AutosaveRecord,
} from './autosave';
import type { SubtitleDocument } from '../../core';
import { SessionContext, type LoadOutcome, type SessionValue } from './context';
import { initialSessionState, sessionReducer } from './reducer';
import { readFileBytes } from './readFileBytes';
import { assignUids } from '../editor/uid';
import { ConfirmDialog } from '../components/ConfirmDialog';

/**
 * Shared document session provider. The Converter loads a file into it; the
 * Editor and QC panel read and mutate the same in-memory document so the three
 * modes stay in sync without prop-drilling. Undo/redo history lives in the
 * reducer (./reducer); the `useSession` hook and context object live in
 * ./context so this file exports only a component.
 */

/** Confirmation shown before a load discards an edited document. */
export const DISCARD_EDITS_MESSAGE =
  'Loading a new file will discard your edits and undo history. Continue?';

/** How long after the last edit to write the autosave snapshot. */
export const AUTOSAVE_DEBOUNCE_MS = 500;
const AUTOSAVE_PREF_KEY = 'cueforge.autosave';

/**
 * Classify an import failure into the closed analytics label set. The source
 * format is unknown on the failure path (a required prop only on the ok path),
 * so `import`'s `error` carries just this coarse reason — never the parser's
 * prose message.
 */
function importErrorKind(err: unknown): ImportErrorKind {
  if (err instanceof UnknownFormatError) return 'unsupported';
  if (err instanceof FileTooLargeError) return 'other';
  return 'parse';
}

/** Read the persisted autosave preference (default on); 'off' disables it. */
function readAutosavePref(): boolean {
  try {
    return globalThis.localStorage?.getItem(AUTOSAVE_PREF_KEY) !== 'off';
  } catch {
    return true;
  }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(sessionReducer, initialSessionState);
  // A previously-autosaved session found on mount, offered for restore.
  const [restorable, setRestorable] = useState<AutosaveRecord | null>(null);
  const [autosaveEnabled, setAutosaveEnabledState] = useState(readAutosavePref);
  // A load blocked by unsaved edits, parked until the styled discard confirm is
  // answered (cue-forge-drt.19 — window.confirm was unstylable in the light theme).
  const [pendingLoad, setPendingLoad] = useState<{ file: File; allowPro: boolean } | null>(null);

  // The load guard reads dirtiness through a ref so `load` stays stable.
  const hasUnsavedEdits = state.dirty && state.loaded !== null;
  const dirtyRef = useRef(hasUnsavedEdits);
  dirtyRef.current = hasUnsavedEdits;

  // True while a real (cue-bearing) restore offer has been surfaced but not yet
  // explicitly resolved. There is ONE autosave slot, so a blank "Start from
  // scratch" (or EMPTY-START) doc must not silently overwrite it before the user
  // has answered the offer (cue-forge-6ut) — the symmetric partner of the mrh
  // rule that a zero-cue doc is never worth offering.
  const unresolvedOfferRef = useRef(false);

  // Resolves with what actually happened so callers can confirm honestly —
  // "Opened X" copy must never fire on a failed or gated load (site-review).
  const doLoad = useCallback(
    async (file: File, allowPro: boolean): Promise<Exclude<LoadOutcome, 'parked'>> => {
      // Read is a distinct failure mode from parse — split it so `import`'s
      // error label is honest (a FileReader failure is 'read', not 'parse').
      let bytes: Uint8Array;
      try {
        bytes = await readFileBytes(file);
      } catch (err) {
        track('import', { outcome: 'fail', error: 'read' });
        dispatch({ type: 'error', message: friendlyLoadError(err, file.name) });
        return 'error';
      }
      try {
        const loaded = await importFile(file.name, bytes, { allowPro });
        dispatch({ type: 'load', loaded });
        setRestorable(null); // a fresh load supersedes any restore offer
        track('import', {
          format: loaded.sourceFormat,
          outcome: 'ok',
          cues: bucketCueCount(loaded.doc.cues.length),
          warnings: bucketCount(loaded.parseWarnings.length),
        });
        return 'loaded';
      } catch (err) {
        // A Pro file opened by a free user is gated with an upgrade prompt, not
        // an error — pro_gate_view (on the prompt mount) covers that intent.
        if (err instanceof ProImportLockedError) {
          dispatch({ type: 'proLocked', format: err.formatName });
          return 'pro-locked';
        }
        track('import', { outcome: 'fail', error: importErrorKind(err) });
        dispatch({ type: 'error', message: friendlyLoadError(err, file.name) });
        return 'error';
      }
    },
    [],
  );

  const load = useCallback(
    async (file: File, allowPro = false): Promise<LoadOutcome> => {
      // Never silently destroy an edited document (cue-forge-xdt): park the load
      // behind a confirm instead. A newer request supersedes a parked one.
      if (dirtyRef.current) {
        setPendingLoad({ file, allowPro });
        return 'parked';
      }
      return doLoad(file, allowPro);
    },
    [doLoad],
  );

  const setDoc = useCallback((doc: SubtitleDocument) => dispatch({ type: 'setDoc', doc }), []);
  // The app's first doc-creating feature (cue-forge-sdv.11.4): transcription's
  // EMPTY-START path mints a `SubtitleDocument` with no source file at all. This
  // mirrors `load` (no undo history, no dirty flag) rather than `setDoc`, since
  // there is no prior document whose loss should ever be undoable; replacing an
  // *existing* document goes through `setDoc` instead so that stays undoable.
  const loadFreshDocument = useCallback(
    (doc: SubtitleDocument) => {
      // EMPTY-only contract (cue-forge-sdv.11.6 final-review fix): unlike
      // `load`, this never bypasses/replaces an already-loaded document — it
      // has no dirty/confirm guard of its own, so a caller invoking it when a
      // document exists would silently clobber it. Callers must only reach
      // for this when `loaded === null`; if one doesn't, this is a no-op
      // rather than a footgun.
      if (state.loaded) return;
      const loaded: LoadedDocument = {
        filename: `Untitled.${doc.metadata.sourceFormat}`,
        sourceFormat: normalizeSourceFormat(doc.metadata.sourceFormat),
        doc,
        encoding: 'utf-8',
        hadBom: false,
        repairs: [],
        parseWarnings: [],
      };
      dispatch({ type: 'load', loaded });
      setRestorable(null); // a fresh document supersedes any restore offer
    },
    [state.loaded],
  );
  const setQcAccepted = useCallback(
    (keys: string[]) => dispatch({ type: 'setQcAccepted', keys }),
    [],
  );
  const undo = useCallback(() => dispatch({ type: 'undo' }), []);
  const redo = useCallback(() => dispatch({ type: 'redo' }), []);
  const markClean = useCallback(() => dispatch({ type: 'markClean' }), []);
  const clear = useCallback(() => dispatch({ type: 'clear' }), []);

  // On mount, surface any autosaved session so the user can restore it. A
  // zero-cue document (the "Start from scratch" blank doc or an EMPTY-START
  // transcription that never got results) is never worth offering — the
  // autosave debounce persists it anyway on load, but restoring it would be a
  // pointless prompt (cue-forge-mrh).
  useEffect(() => {
    if (!autosaveSupported() || !readAutosavePref()) return;
    let active = true;
    void loadAutosave()
      .then((rec) => {
        if (active && rec && rec.loaded.doc.cues.length > 0) {
          setRestorable(rec);
          unresolvedOfferRef.current = true;
        }
      })
      .catch((err) => {
        // A malformed record must not surface as an unhandled rejection — log it
        // and proceed as if there were no autosave (cue-forge-6ut).
        console.warn('Ignoring unreadable autosave record:', err);
      });
    return () => {
      active = false;
    };
  }, []);

  // Debounced local autosave of the working document (never uploaded).
  useEffect(() => {
    if (!autosaveEnabled || !autosaveSupported() || !state.loaded) return;
    const loaded = state.loaded;
    // Don't let a blank doc overwrite the slot while a real offer is unresolved
    // (cue-forge-6ut). Once the working doc has real cues the user has committed
    // to new work, so clear the guard and resume autosaving normally.
    if (unresolvedOfferRef.current) {
      if (loaded.doc.cues.length === 0) return;
      unresolvedOfferRef.current = false;
    }
    const id = setTimeout(() => void saveAutosave(loaded), AUTOSAVE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [state.loaded, autosaveEnabled]);

  const restore = useCallback(() => {
    unresolvedOfferRef.current = false; // explicit resolution — the slot may be reused
    setRestorable((rec) => {
      // Backfill cue uids (cue-forge-9ww.7) in case the autosave predates them,
      // so durable selection works after restore. Idempotent for fresh saves.
      if (rec)
        dispatch({ type: 'load', loaded: { ...rec.loaded, doc: assignUids(rec.loaded.doc) } });
      return null;
    });
  }, []);

  const discardRestore = useCallback(() => {
    unresolvedOfferRef.current = false;
    setRestorable(null);
    void clearAutosave();
  }, []);

  const setAutosaveEnabled = useCallback((on: boolean) => {
    setAutosaveEnabledState(on);
    try {
      globalThis.localStorage?.setItem(AUTOSAVE_PREF_KEY, on ? 'on' : 'off');
    } catch {
      /* storage unavailable */
    }
    // Disabling autosave forgets the local copy (confidential-content escape hatch).
    if (!on) {
      unresolvedOfferRef.current = false;
      setRestorable(null);
      void clearAutosave();
    }
  }, []);

  // Warn before the page unloads while edits are unsaved; the handler is only
  // registered while dirty so a clean session never blocks navigation.
  useEffect(() => {
    if (!hasUnsavedEdits) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [hasUnsavedEdits]);

  const value = useMemo<SessionValue>(
    () => ({
      loaded: state.loaded,
      error: state.error,
      proImportLocked: state.proImportLocked,
      load,
      setDoc,
      loadFreshDocument,
      setQcAccepted,
      undo,
      redo,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      dirty: hasUnsavedEdits,
      markClean,
      clear,
      restorable,
      restore,
      discardRestore,
      autosaveEnabled,
      setAutosaveEnabled,
    }),
    [
      state,
      load,
      setDoc,
      loadFreshDocument,
      setQcAccepted,
      undo,
      redo,
      markClean,
      clear,
      hasUnsavedEdits,
      restorable,
      restore,
      discardRestore,
      autosaveEnabled,
      setAutosaveEnabled,
    ],
  );

  return (
    <SessionContext.Provider value={value}>
      {children}
      {pendingLoad && (
        <ConfirmDialog
          title="Discard edits?"
          message={DISCARD_EDITS_MESSAGE}
          confirmLabel="Discard and load"
          onConfirm={() => {
            const { file, allowPro } = pendingLoad;
            setPendingLoad(null);
            void doLoad(file, allowPro);
          }}
          onCancel={() => setPendingLoad(null)}
        />
      )}
    </SessionContext.Provider>
  );
}
